// Шестерёнка администрирования в топбаре.
//
// Зачем: в левом меню было 25 административных пунктов подряд
// (docs/specs/navigation-redesign-2026-07.md §5). Это настройки, а не
// ежедневная работа — им место под шестерёнкой рядом с именем пользователя,
// как в Workday, BambooHR, Linear и GitHub.
//
// В левом меню из этого блока остаётся только «Поддержка · Пульт»: им
// администратор пользуется каждый день.
//
// Видимость каждой строки считает ModuleContext по ключу модуля — состав
// пунктов не дублирует RBAC, а опирается на него.
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    AlarmClock, AlertTriangle, Bot, Building2, ClipboardCheck, Code2, Compass, FileText,
    Flag, GitBranch, History, Link2, ListTodo, Monitor, Palette, Plug, Settings, Shield,
    ShieldCheck, Telescope, Wallet,
} from 'lucide-react'
import { useModules } from '../../contexts/ModuleContext'
import { useAuth } from '../../contexts/AuthContext'

export const SETTINGS_GROUPS = [
    {
        title: 'Настройки компании',
        items: [
            { to: 'rbac', icon: Shield, label: 'Управление доступами' },
            { to: 'admin-organizations', icon: Building2, label: 'Организации' },
            { to: 'legal-templates', icon: FileText, label: 'Шаблоны документов' },
            { to: 'flags', icon: Flag, label: 'Feature Flags' },
        ],
    },
    {
        title: 'Интеграции',
        items: [
            { to: 'hh-settings', icon: Link2, label: 'HH.ru API' },
            { to: 'integrations', icon: Plug, label: 'Integration Hub' },
            { to: 'admin-modules', icon: Settings, label: 'Module Store' },
        ],
    },
    {
        title: 'Контроль и безопасность',
        items: [
            { to: 'admin/security', icon: Shield, label: 'Дашборд безопасности' },
            { to: 'admin/compliance-overview', icon: Shield, label: 'AML Compliance' },
            { to: 'admin/dsar', icon: Shield, label: 'Запросы данных сотрудника' },
            { to: 'admin/termination-reviews', icon: AlertTriangle, label: 'AI-проверка увольнений' },
            { to: 'workflow-history', icon: History, label: 'История процессов' },
            { to: 'track-outcomes', icon: ShieldCheck, label: 'Исходы треков' },
            { to: 'inventory-visibility', icon: Monitor, label: 'Видимость оборудования' },
        ],
    },
    {
        title: 'Служебное',
        items: [
            { to: 'mission-control', icon: Bot, label: 'Mission Control' },
            { to: 'agent-tasks', icon: ListTodo, label: 'Задачи агента' },
            { to: 'ai-watch', icon: Telescope, label: 'AI Watch' },
            { to: 'system-costs', icon: Wallet, label: 'Стоимость системы' },
            { to: 'itadmin', icon: Monitor, label: 'IT-панель' },
            { to: 'admin/claude-plugins', icon: Bot, label: 'Claude Plugins' },
            { to: 'admin/ai-drafts', icon: ClipboardCheck, label: 'AI-черновики' },
            { to: 'admin/watchers', icon: AlarmClock, label: 'Watchers' },
            { to: 'admin/cold-start', icon: Compass, label: 'Cold-start' },
            { to: 'business-processes', icon: GitBranch, label: 'Бизнес-процессы' },
            { to: 'style-guide', icon: Palette, label: 'Design System' },
            { to: 'api-docs', icon: Code2, label: 'API Reference' },
        ],
    },
]

/** Плоский список маршрутов шестерёнки — чтобы не дублировать их в левом меню. */
export const SETTINGS_ROUTES = SETTINGS_GROUPS.flatMap(g => g.items.map(i => i.to))

export default function SettingsMenu({ effectiveUser }) {
    const [open, setOpen] = useState(false)
    const boxRef = useRef(null)
    const navigate = useNavigate()
    const { isModuleVisible } = useModules()
    const { user } = useAuth()
    const viewer = effectiveUser || user

    useEffect(() => {
        if (!open) return undefined
        const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false) }
        const onEsc = (e) => {
            if (e.key !== 'Escape' || e.defaultPrevented) return
            e.preventDefault()
            setOpen(false)
        }
        document.addEventListener('mousedown', onClick)
        document.addEventListener('keydown', onEsc)
        return () => {
            document.removeEventListener('mousedown', onClick)
            document.removeEventListener('keydown', onEsc)
        }
    }, [open])

    const groups = SETTINGS_GROUPS
        .map(g => ({ ...g, items: g.items.filter(i => isModuleVisible(i.to, viewer)) }))
        .filter(g => g.items.length > 0)

    // Ни одной доступной настройки — шестерёнку не показываем вовсе.
    if (groups.length === 0) return null

    const go = (to) => { setOpen(false); navigate(`/${to}`) }

    return (
        <div style={{ position: 'relative' }} ref={boxRef}>
            <button
                type="button"
                className="topbar__icon-btn"
                onClick={() => setOpen(v => !v)}
                aria-expanded={open}
                aria-label="Настройки и администрирование"
                title="Настройки и администрирование"
            >
                <Settings size={18} />
            </button>

            {open && (
                <div
                    data-app-overlay="settings-menu"
                    className="settings-menu__panel"
                >
                    {groups.map((group, gi) => (
                        <div key={group.title} style={{ marginTop: gi === 0 ? 0 : 10 }}>
                            <div
                                className="text-xxxs text-muted fw-700"
                                style={{ padding: '6px 10px 4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}
                            >
                                {group.title}
                            </div>
                            {group.items.map(item => {
                                const Icon = item.icon
                                return (
                                    <button
                                        key={item.to}
                                        type="button"
                                        onClick={() => go(item.to)}
                                        className="flex items-center gap-8"
                                        style={{
                                            width: '100%',
                                            padding: '8px 10px',
                                            border: 'none',
                                            background: 'transparent',
                                            borderRadius: 'var(--radius-sm)',
                                            color: 'var(--color-text)',
                                            fontSize: '0.875rem',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            transition: 'background 0.15s',
                                        }}
                                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-bg-soft)' }}
                                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                                    >
                                        <Icon size={15} className="text-muted" />
                                        <span>{item.label}</span>
                                    </button>
                                )
                            })}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
